// src/store/slices/tenantSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import tenantService from '../../api/tenantService';

// Async thunks
export const fetchTenants = createAsyncThunk(
  'tenants/fetchTenants',
  async (_, { rejectWithValue }) => {
    try {
      return await tenantService.getAllTenants();
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const createTenant = createAsyncThunk(
  'tenants/createTenant',
  async (tenantData, { rejectWithValue }) => {
    try {
      return await tenantService.createTenant(tenantData);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updateTenant = createAsyncThunk(
  'tenants/updateTenant',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await tenantService.updateTenant(id, data);
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deleteTenant = createAsyncThunk(
  'tenants/deleteTenant',
  async (id, { rejectWithValue }) => {
    try {
      await tenantService.deleteTenant(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

// Initial state
const initialState = {
  tenants: [],
  loading: false,
  error: null,
  filterType: 'all', // 'all', 'master', 'regular'
};

// Slice
const tenantSlice = createSlice({
  name: 'tenants',
  initialState,
  reducers: {
    setFilterType: (state, action) => {
      state.filterType = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchTenants
      .addCase(fetchTenants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTenants.fulfilled, (state, action) => {
        state.tenants = action.payload;
        state.loading = false;
      })
      .addCase(fetchTenants.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // createTenant
      .addCase(createTenant.fulfilled, (state, action) => {
        state.tenants.push(action.payload);
      })
      // updateTenant
      .addCase(updateTenant.fulfilled, (state, action) => {
        const index = state.tenants.findIndex(tenant => tenant.id === action.payload.id);
        if (index !== -1) {
          state.tenants[index] = action.payload;
        }
      })
      // deleteTenant
      .addCase(deleteTenant.fulfilled, (state, action) => {
        state.tenants = state.tenants.filter(tenant => tenant.id !== action.payload);
      });
  },
});

// Actions
export const { setFilterType } = tenantSlice.actions;

// Selectors
export const selectAllTenants = (state) => state.tenants.tenants;
export const selectFilteredTenants = (state) => {
  const { tenants, filterType } = state.tenants;
  
  if (filterType === 'all') {
    return tenants;
  } else if (filterType === 'master') {
    return tenants.filter(tenant => tenant.is_master);
  } else if (filterType === 'regular') {
    return tenants.filter(tenant => !tenant.is_master);
  }
  
  return tenants;
};
export const selectTenantById = (state, id) => 
  state.tenants.tenants.find(tenant => tenant.id === id);
export const selectTenantLoading = (state) => state.tenants.loading;
export const selectTenantError = (state) => state.tenants.error;
export const selectFilterType = (state) => state.tenants.filterType;

// Reducer
export default tenantSlice.reducer;